import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore'
import {
  auditDeletionTimestamp,
  auditRetentionMode,
  buildAuditSearchPrefixes,
  maskAuditPayload,
} from './auditLedgerService'
import { deriveAuditCategory } from './auditCategories'
import { firestoreDb } from './firebase'
import type { AutomationActor } from './automationService'
import type { AutomationTriggerEvent } from '../types/automation'

const AUTHORITATIVE_TRIGGER_TYPES = new Set<AutomationTriggerEvent['type']>([
  'NODE_COMPLETED',
  'AI_ESSAY_GENERATED',
  'AI_REFINE_USED',
  'FOCUS_BLOCK_COMPLETED',
  'SPIN_WON',
  'CHAT_MESSAGE_POSTED',
])

const asNodeType = (event: AutomationTriggerEvent): string => {
  switch (event.type) {
    case 'NODE_COMPLETED':
      return event.payload.nodeKind
    case 'AI_ESSAY_GENERATED':
    case 'AI_REFINE_USED':
      return 'AI_GENERATION'
    case 'FOCUS_BLOCK_COMPLETED':
      return 'FOCUS_BLOCK'
    case 'SPIN_WON':
      return 'SPIN_REWARD'
    case 'CHAT_MESSAGE_POSTED':
      return 'CHAT_MESSAGE'
    default:
      return 'AUTOMATION_EVENT'
  }
}

const buildSummary = (event: AutomationTriggerEvent): string => {
  switch (event.type) {
    case 'NODE_COMPLETED':
      return `${event.payload.nodeKind} "${event.payload.title ?? event.payload.nodeId}" completed.`
    case 'AI_ESSAY_GENERATED':
      return 'AI Generator created a fresh draft.'
    case 'AI_REFINE_USED':
      return 'AI Generator completed a refine pass.'
    case 'FOCUS_BLOCK_COMPLETED':
      return `Focus block completed after ${event.payload.minutes} minute${event.payload.minutes === 1 ? '' : 's'}.`
    case 'SPIN_WON':
      return `${event.payload.tier} spin awarded ${event.payload.credits ?? 0} credits.`
    case 'CHAT_MESSAGE_POSTED':
      return `Chat message posted${event.payload.textPreview ? `: ${event.payload.textPreview}` : '.'}`
    default:
      return 'Automation event recorded.'
  }
}

const buildEnvelope = (event: AutomationTriggerEvent) => {
  switch (event.type) {
    case 'NODE_COMPLETED':
      return {
        action: 'NODE_COMPLETED',
        actionType: 'NODE_COMPLETED',
        activityType: 'UPDATE',
        targetId: event.payload.nodeId,
        targetTitle: event.payload.title ?? 'Completed node',
        board: event.payload.boardKey ?? null,
      }
    case 'AI_ESSAY_GENERATED':
      return {
        action: 'AI_ESSAY_GENERATED',
        actionType: 'AI_ESSAY_GENERATED',
        activityType: 'UPDATE',
        targetId: event.payload.sourceId ?? event.eventId,
        targetTitle: 'AI draft',
        board: null,
      }
    case 'AI_REFINE_USED':
      return {
        action: 'AI_REFINE_USED',
        actionType: 'AI_REFINE_USED',
        activityType: 'UPDATE',
        targetId: event.payload.sourceId ?? event.eventId,
        targetTitle: 'AI refine',
        board: null,
      }
    case 'FOCUS_BLOCK_COMPLETED':
      return {
        action: 'FOCUS_BLOCK_COMPLETED',
        actionType: 'FOCUS_BLOCK_COMPLETED',
        activityType: 'UPDATE',
        targetId: event.payload.subNodeId ?? event.eventId,
        targetTitle: 'Focus block',
        board: null,
      }
    case 'SPIN_WON':
      return {
        action: 'SPIN_WON',
        actionType: 'SPIN_WON',
        activityType: 'UPDATE',
        targetId: event.payload.prizeKey ?? event.eventId,
        targetTitle: `${event.payload.tier} spin reward`,
        board: null,
      }
    case 'CHAT_MESSAGE_POSTED':
      return {
        action: 'CHAT_MESSAGE_POSTED',
        actionType: 'CHAT_MESSAGE_POSTED',
        activityType: 'CREATE',
        targetId: event.payload.messageId,
        targetTitle: event.payload.textPreview?.trim() || 'Chat message',
        board: null,
      }
    default:
      return null
  }
}

export const isAuthoritativeAutomationEvent = (
  event: AutomationTriggerEvent,
): boolean => AUTHORITATIVE_TRIGGER_TYPES.has(event.type)

export const publishAutomationEventToWorkspaceActivity = async (
  event: AutomationTriggerEvent,
  actor: AutomationActor,
): Promise<boolean> => {
  if (!isAuthoritativeAutomationEvent(event)) return false
  const envelope = buildEnvelope(event)
  if (!envelope) return false

  await runTransaction(firestoreDb, async (transaction) => {
    const workspace = await transaction.get(doc(firestoreDb, 'workspaces', event.workspaceId))
    const workspaceData = workspace.data()
    const payload = maskAuditPayload({
      ...event.payload,
      automationSourceEventId: event.eventId,
    }) as Record<string, unknown>
    const actorName = actor.name && !actor.name.includes('@') ? actor.name : 'Workspace member'
    const actionType = envelope.actionType
    const deletionTimestamp = auditDeletionTimestamp(workspaceData)
    const nodeType = asNodeType(event)
    transaction.set(doc(collection(firestoreDb, 'workspace_activity')), {
      workspaceId: event.workspaceId,
      actorId: actor.id,
      actorName,
      action: envelope.action,
      actionType,
      activityType: envelope.activityType,
      targetId: envelope.targetId,
      nodeType,
      targetType: nodeType,
      targetTitle: envelope.targetTitle,
      summary: buildSummary(event),
      maskedPayload: payload,
      metadata: payload,
      snapshot: null,
      auditCategory: deriveAuditCategory({ action: envelope.action, actionType, activityType: envelope.activityType, nodeType }),
      auditSearchPrefixes: buildAuditSearchPrefixes(envelope.targetTitle, buildSummary(event), envelope.action, actorName),
      retentionMode: auditRetentionMode(workspaceData),
      board: envelope.board,
      createdAt: serverTimestamp(),
      ...(deletionTimestamp ? { deletionTimestamp } : {}),
    })
  })

  return true
}
